import { Eye, EyeOff, Pencil, Search } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { LIMITS, type CatalogEntryView } from "../../../shared/contracts.js";
import { useCatalogList, useSetCatalogEntryHidden, useUpdateCatalogEntry } from "../../api/catalogQueries.js";
import { isClientApiError } from "../../api/http.js";
import { AsyncPanel, describeError } from "../../ui/AsyncPanel.js";
import { Button } from "../../ui/Button.js";
import { formatInstant } from "../cases/detail/ActivityTimeline.js";
import { ORDER_FORM_MESSAGES } from "../cases/order-form/OrderForm.js";
import { formatCount } from "../cases/presentation.js";
import { SidedPageShell } from "../wholesale/SidedPageShell.js";

/**
 * `/parts/catalog` and `/wholesale/catalog` — the parts catalog (spec
 * §6.3): what the counter has typed before, one row per part number, for
 * Parts to search, correct and tidy.
 *
 * The page sits in the shell of the side it was opened from (the address
 * says which — `SidedPageShell`), beside that side's order form and the
 * report, and the shell turns every other role away.
 * A search box filters by number or description (debounced, like the list
 * search); "Show hidden" brings hidden entries back into the list. Each
 * row shows the number as first typed, the description the forms
 * auto-fill, how many lines have used it, and who last edited it and
 * when. Edit description opens the description in place; Save sends the
 * new words under the entry's `updatedAt`, and a 409 (someone edited it
 * meanwhile) says so and reloads the list. Hide/Unhide is one click and
 * one request. Nothing here rewrites a line already on an order, and
 * nothing is ever deleted. Parts and Parts manager only; every other role
 * is sent to its own workspace.
 */

const SEARCH_DEBOUNCE_MS = 250;

export function CatalogPage() {
  return (
    <SidedPageShell activeDestination="catalog" mainClassName="catalog-page">
      <CatalogContent />
    </SidedPageShell>
  );
}

function CatalogContent() {
  const titleId = useId();
  const searchId = useId();
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [includeHidden, setIncludeHidden] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [alert, setAlert] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const list = useCatalogList({ q: query, includeHidden });
  const entries = list.data?.pages.flatMap((page) => page.items) ?? [];

  const commitSearch = (value: string) => setQuery(value.trim().slice(0, LIMITS.search.max));
  const onSearchChange = (value: string) => {
    setDraft(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => commitSearch(value), SEARCH_DEBOUNCE_MS);
  };
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const empty = list.data !== undefined && entries.length === 0;

  return (
    <div className="catalog" aria-labelledby={titleId}>
      <header className="catalog__header">
        <h1 id={titleId} className="catalog__title">
          Parts catalog
        </h1>
        <p className="catalog__lead">
          Every part number the counter has ordered, with the description the order forms fill in. The first description
          typed is the one kept here; change it when it is wrong. Lines already on orders are never rewritten.
        </p>
      </header>

      <div className="catalog__bar">
        <div className="catalog__search" role="search">
          <Search className="catalog__search-icon" aria-hidden="true" focusable="false" />
          <input
            id={searchId}
            type="search"
            className="text-input catalog__search-input"
            data-size="desktop"
            aria-label="Search the catalog"
            placeholder="Part number or description"
            autoComplete="off"
            spellCheck={false}
            value={draft}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              if (timer.current) clearTimeout(timer.current);
              commitSearch(draft);
            }}
          />
        </div>
        <label className="catalog__toggle">
          <input
            type="checkbox"
            className="checkbox-input"
            checked={includeHidden}
            onChange={(event) => setIncludeHidden(event.target.checked)}
          />
          <span>Show hidden</span>
        </label>
      </div>

      <p role="status" aria-live="polite" className={`inspector__announcement${announcement ? "" : " inspector__announcement--empty"}`}>
        {announcement}
      </p>
      {alert ? (
        <p role="alert" className="form-alert catalog__alert">
          {alert}
        </p>
      ) : null}

      <AsyncPanel
        isLoading={list.isPending}
        error={list.isError && list.data === undefined ? list.error : undefined}
        refreshError={list.isError && list.data !== undefined ? list.error : undefined}
        refreshTitle="Couldn't refresh the catalog."
        isEmpty={empty}
        onRetry={() => {
          void list.refetch();
        }}
        loadingLabel="Loading the catalog…"
        emptyTitle={query === "" ? "No parts in the catalog yet" : "No parts match"}
        emptyDescription={
          query === ""
            ? "Part numbers are added here as orders are saved."
            : "Try another part number or a word from the description."
        }
      >
        <table className="report__table catalog__table" aria-label="Parts catalog">
          <thead>
            <tr>
              <th scope="col">Part number</th>
              <th scope="col">Description</th>
              <th scope="col" className="report__numeric">
                Times used
              </th>
              <th scope="col">Last edited</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <CatalogRow
                key={entry.normalizedPartNumber}
                entry={entry}
                onSaved={(message) => {
                  setAlert(null);
                  setAnnouncement(message);
                }}
                onStale={(message) => {
                  setAlert(message);
                  void list.refetch();
                }}
              />
            ))}
          </tbody>
        </table>
        {list.hasNextPage ? (
          <div className="catalog__more">
            <Button variant="secondary" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              Show more
            </Button>
          </div>
        ) : null}
      </AsyncPanel>
    </div>
  );
}

interface CatalogRowProps {
  entry: CatalogEntryView;
  onSaved: (announcement: string) => void;
  /** A 409: someone edited the entry meanwhile; the caller reloads the list. */
  onStale: (message: string) => void;
}

function CatalogRow({ entry, onSaved, onStale }: CatalogRowProps) {
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(entry.description);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const editRef = useRef<HTMLButtonElement>(null);
  const update = useUpdateCatalogEntry();
  const setHidden = useSetCatalogEntryHidden();
  const busy = update.isPending || setHidden.isPending;

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = () => {
    setDescription(entry.description);
    setFieldError(null);
    setRowError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setFieldError(null);
    editRef.current?.focus();
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = description.trim();
    if (trimmed.length === 0) {
      setFieldError(ORDER_FORM_MESSAGES.description);
      return;
    }
    if (trimmed.length > LIMITS.description.max) {
      setFieldError(ORDER_FORM_MESSAGES.descriptionLength);
      return;
    }
    setFieldError(null);
    update.mutate(
      { normalizedPartNumber: entry.normalizedPartNumber, input: { description: trimmed, expectedUpdatedAt: entry.updatedAt } },
      {
        onSuccess: () => {
          setEditing(false);
          onSaved(`Description saved for ${entry.partNumber}.`);
        },
        onError: (error) => {
          if (isClientApiError(error) && error.code === "STALE_WRITE") {
            setEditing(false);
            onStale(`${entry.partNumber} changed after you opened it. The catalog was reloaded — check it and try again.`);
            return;
          }
          setRowError(describeError(error).description);
        },
      },
    );
  };

  const toggleHidden = () => {
    setRowError(null);
    setHidden.mutate(
      { normalizedPartNumber: entry.normalizedPartNumber, hidden: !entry.hidden },
      {
        onSuccess: (updated) => onSaved(`${entry.partNumber} ${updated.hidden ? "hidden" : "shown again"}.`),
        onError: (error) => setRowError(describeError(error).description),
      },
    );
  };

  return (
    <tr className="catalog__row" data-hidden={entry.hidden ? "true" : undefined}>
      <td className="report__mono catalog__number">
        <span>{entry.partNumber}</span>
        {entry.hidden ? <span className="catalog__flag">Hidden</span> : null}
      </td>
      <td className="catalog__description">
        {editing ? (
          <form className="catalog__edit" onSubmit={save} noValidate>
            <label htmlFor={inputId} className="visually-hidden">
              Description
            </label>
            <input
              ref={inputRef}
              id={inputId}
              className="text-input"
              data-size="desktop"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
                if (fieldError) setFieldError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") cancel();
              }}
              aria-describedby={fieldError ? `${inputId}-error` : undefined}
              aria-invalid={fieldError ? true : undefined}
              maxLength={LIMITS.description.max + 10}
              disabled={busy}
            />
            {fieldError ? (
              <p id={`${inputId}-error`} className="field__error">
                {fieldError}
              </p>
            ) : null}
            <div className="catalog__edit-actions">
              <Button type="submit" variant="primary" busy={update.isPending} disabled={busy}>
                Save
              </Button>
              <Button variant="secondary" onClick={cancel} disabled={busy}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          entry.description
        )}
        {rowError ? (
          <p role="alert" className="field__error">
            {rowError}
          </p>
        ) : null}
      </td>
      <td className="report__numeric">{formatCount(entry.timesUsed)}</td>
      <td className="catalog__edited">
        <span className="catalog__edited-when">{formatInstant(entry.updatedAt)}</span>
        {entry.updatedByName ? <span className="catalog__edited-by">by {entry.updatedByName}</span> : null}
      </td>
      <td className="catalog__actions">
        {!editing ? (
          <Button ref={editRef} variant="quiet" icon={<Pencil />} onClick={startEditing} disabled={busy}>
            {"Edit description "}
            <span className="visually-hidden">of {entry.partNumber}</span>
          </Button>
        ) : null}
        <Button
          variant="quiet"
          icon={entry.hidden ? <Eye /> : <EyeOff />}
          onClick={toggleHidden}
          busy={setHidden.isPending}
          disabled={busy || editing}
        >
          {entry.hidden ? "Unhide " : "Hide "}
          <span className="visually-hidden">{entry.partNumber}</span>
        </Button>
      </td>
    </tr>
  );
}
